import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [topQRCodes, setTopQRCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      const response = await axios.get('/api/analytics/overview');
      setStats(response.data.statistics);
      setTopQRCodes(response.data.topQRCodes || []);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading your dashboard...</div>;
  }

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="container">
      <div className="dashboard-welcome">
        <h1>{greeting()}, <span>{user?.name || 'there'}</span>! 👋</h1>
        <p>Here's what's happening with your QR codes today.</p>
      </div>

      <div className="dashboard-header">
        <div className="quick-actions">
          <Link to="/qr-codes/create" className="btn btn-primary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Create QR Code
          </Link>
          <Link to="/qr-codes" className="btn btn-secondary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/>
              <rect x="14" y="3" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
            </svg>
            View All
          </Link>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card stagger-1">
          <h3>Total QR Codes</h3>
          <div className="value">{stats?.total_qr_codes || 0}</div>
        </div>
        <div className="stat-card stagger-2">
          <h3>Total Clicks</h3>
          <div className="value">{stats?.total_clicks || 0}</div>
        </div>
        <div className="stat-card stagger-3">
          <h3>Today's Clicks</h3>
          <div className="value">{stats?.today_clicks || 0}</div>
        </div>
        <div className="stat-card stagger-4">
          <h3>Total Revenue</h3>
          <div className="value">${parseFloat(stats?.total_charged || 0).toFixed(2)}</div>
        </div>
        <div className="stat-card stagger-5" style={{ 
          background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.08) 0%, rgba(88, 86, 214, 0.08) 100%)',
          borderColor: 'rgba(0, 122, 255, 0.2)'
        }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>🍎</span> iOS
          </h3>
          <div className="value" style={{ background: 'linear-gradient(135deg, #007AFF, #5856D6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {stats?.ios_clicks || 0}
          </div>
        </div>
        <div className="stat-card stagger-6" style={{ 
          background: 'linear-gradient(135deg, rgba(61, 220, 132, 0.08) 0%, rgba(52, 168, 83, 0.08) 100%)',
          borderColor: 'rgba(61, 220, 132, 0.2)'
        }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>🤖</span> Android
          </h3>
          <div className="value" style={{ background: 'linear-gradient(135deg, #3DDC84, #34A853)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {stats?.android_clicks || 0}
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Top Performing QR Codes</h2>
        {topQRCodes.length > 0 ? (
          <div className="qr-table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Rank</th>
                  <th>Title</th>
                  <th>Short Code</th>
                  <th>Clicks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {topQRCodes.map((qr, index) => (
                  <tr key={qr.id}>
                    <td>
                      <span className={`qr-rank ${index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'bronze' : ''}`}>
                        {index + 1}
                      </span>
                    </td>
                    <td>
                      <strong>{qr.title || 'Untitled'}</strong>
                    </td>
                    <td>
                      <code>{qr.short_code}</code>
                    </td>
                    <td>
                      <span style={{ fontWeight: 600, color: 'var(--accent-primary)' }}>
                        {qr.click_count || 0}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Link to={`/qr-codes/view/${qr.id}`} className="btn btn-secondary" style={{ padding: '8px 12px', fontSize: '13px' }}>
                          View
                        </Link>
                        <Link to={`/analytics/${qr.id}`} className="btn btn-ghost" style={{ padding: '8px 12px', fontSize: '13px' }}>
                          Analytics
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
            </div>
            <h3>No QR codes yet</h3>
            <p>Create your first QR code to start tracking clicks and generating revenue.</p>
            <Link to="/qr-codes/create" className="btn btn-primary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Create Your First QR Code
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
